import React from 'react';
import { Group, Shape } from 'react-konva';
import { inwardForSide, outwardForSide, rotateVec, Vec } from '../lib/geometry';

export interface ConnectionPreviewProps {
  start: Vec;
  end: Vec;
  startSide: 'top'|'right'|'bottom'|'left';
  endSide: 'top'|'right'|'bottom'|'left';
  startRotation?: number;
  endRotation?: number;
  snapped: boolean;
  color?: string;
}

export const ConnectionPreview: React.FC<ConnectionPreviewProps> = ({
  start,
  end,
  startSide,
  endSide,
  startRotation = 0,
  endRotation = 0,
  snapped,
  color = '#3b82f6',
}) => {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const dist = Math.hypot(dx, dy) || 1;
  const baseLen = Math.min(160, Math.max(40, dist * 0.35));

  const startDir = rotateVec(outwardForSide(startSide).x, outwardForSide(startSide).y, startRotation);
  const endDir = snapped
    ? rotateVec(inwardForSide(endSide).x, inwardForSide(endSide).y, endRotation)
    : startDir;

  const c1 = { x: start.x + startDir.x * baseLen, y: start.y + startDir.y * baseLen };
  const c2 = { x: end.x - endDir.x * baseLen, y: end.y - endDir.y * baseLen };

  // Arrowhead direction: outward while not snapped, tangent on snapped
  const dirX = snapped ? (end.x - c2.x) : startDir.x;
  const dirY = snapped ? (end.y - c2.y) : startDir.y;
  const len = Math.hypot(dirX, dirY) || 1;
  const ux = dirX / len;
  const uy = dirY / len;
  const pointerLength = 12;
  const pointerWidth = 8;
  const baseX = end.x - ux * pointerLength;
  const baseY = end.y - uy * pointerLength;
  const px = -uy * (pointerWidth / 2);
  const py = ux * (pointerWidth / 2);
  const leftX = baseX + px;
  const leftY = baseY + py;
  const rightX = baseX - px;
  const rightY = baseY - py;

  return (
    <Group listening={false}>
      <Shape
        sceneFunc={(context, shape) => {
          context.beginPath();
          context.moveTo(start.x, start.y);
          context.bezierCurveTo(c1.x, c1.y, c2.x, c2.y, end.x, end.y);
          context.fillStrokeShape(shape);
        }}
        stroke={color}
        strokeWidth={2}
        dash={[5, 5]}
        opacity={0.85}
        perfectDrawEnabled={false}
      />
      <Shape
        sceneFunc={(context, shape) => {
          context.beginPath();
          context.moveTo(end.x, end.y);
          context.lineTo(leftX, leftY);
          context.lineTo(rightX, rightY);
          context.closePath();
          context.fillStrokeShape(shape);
        }}
        fill={color}
        stroke={color}
        strokeWidth={2}
        opacity={0.85}
        perfectDrawEnabled={false}
      />
    </Group>
  );
};


